import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";

import Checkbox from "../components/Checkbox";
import Icon from "../components/Icon";
import PromotionSelector from "../components/PromotionSelector";
import Select from "../components/Select";
import TextInput from "../components/TextInput";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import {
  IS_DEV,
  LAND_SITUATION_NSW_DESKTOP_HL,
  LAND_SITUATION_NSW_DESKTOP_KDR,
  LAND_SITUATION_NSW_DESKTOP_LOOKING,
  LAND_SITUATION_NSW_DESKTOP_VACANT,
  LAND_SITUATION_NSW_KIOSK_HAVE,
  LAND_SITUATION_NSW_KIOSK_LOOKING,
  LEADS_STATUS_NSW_BROWSING,
  LEADS_STATUS_NSW_NEW,
  LEADS_STATUS_NSW_OLD,
} from "../resources/constants";
import { getPageTitle } from "../resources/routes";
import { BuildSituation,Promotion,RoutePath } from "../types";
import { scrollToTop, scrollToWithOffset } from "../util";

const NswFinalDetailsScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [regionError, setRegionError] = useState("");
  const [buildSituationError, setBuildSituationError] = useState("");

  useEffect(() => {
    scrollToTop();
    navContext.setOfferTickerVisible(false);
    navContext.setFooterModePrimary(); // In case the user presses browser-back
    navContext.setNextButtonEnabled(false);
    navContext.setRestartButtonVisible(false);
    navContext.setSubtotalVisible(false);
    navContext.setNextBtnOnClick(() => {
      if(userContext.isKioskMode){
        if(!IS_DEV){
          try {
          // @ts-ignore
            window.gtag("event", "SUCCEEDED", {
              "event_category": "ENQUIRY", 
              "event_label": "pyht-sign-up"
            });
            // @ts-ignore
            window.spdt("signup", {
              event_id: "pyht-sign-up",
            });
            // @ts-ignore
            window.ttq.track("SubmitForm", {
              "contents": [
                {
                  "content_id": window.location.href,
                  "content_type": "Form Submitted Successfully",
                  "content_name": "pyht-final-details"
                }
              ]
            });
            // @ts-ignore
            window.fbq("track", "SubmitApplication");
            // @ts-ignore
            uet_report_conversion();
            // @ts-ignore
            if(window.adroll !== undefined){
            // @ts-ignore
              window.adroll.identify_email(userContext.email);
            }
            // @ts-ignore
            if(window.rdt !== undefined){
            // @ts-ignore
              window.rdt("track", "Lead");
            }
          } catch (e) {
            console.error(e);
          }
        }
        navContext.goNext();
      }else{
        submitForm();
      }
    });
  }, []);

  useEffect(() => {
    if (apiContext.isLoading) {
      navContext.setNextButtonEnabled(false);
      return;
    }
    if(userContext.isKioskMode){
      if (
        userContext.firstName !== null && userContext.firstName.trim() !== "" &&
        userContext.lastName !== null && userContext.lastName.trim() !== "" &&
        userContext.email !== null && userContext.email.trim() !== "" &&
        /\S+@\S+\.\S+/.test(userContext.email) && // Email regex
        userContext.phone !== null && userContext.phone !== "" && 
        apiContext.region !== null &&
        userContext.consent
      ) {
        navContext.setNextButtonEnabled(true);
      } else {
        navContext.setNextButtonEnabled(false);
      }
    }else{
      if (
        userContext.firstName !== null && userContext.firstName.trim() !== "" &&
        userContext.lastName !== null && userContext.lastName.trim() !== "" &&
        userContext.email !== null && userContext.email.trim() !== "" &&
        /\S+@\S+\.\S+/.test(userContext.email) && // Email regex
        userContext.phone !== null && userContext.phone !== "" &&
        apiContext.region !== null &&
        userContext.consent
      ) {
        navContext.setNextButtonEnabled(true);
      } else {
        navContext.setNextButtonEnabled(false);
      }
    }
  }, [
    userContext.firstName,
    userContext.lastName,
    userContext.email,
    userContext.phone,
    apiContext.region,
    userContext.consent,
    apiContext.isLoading,
  ]);

  const submitForm = async () => {
    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setPhoneError("");
    setRegionError("");
    setBuildSituationError("");
    try {
      const result = await apiContext.emailSummary({
        name: userContext.firstName,
        surname: userContext.lastName,
        email: userContext.email,
        phone: userContext.phone,
        remember: userContext.rememberDetails,
        opt_in: false,
        region: apiContext.region,
        email_secondary: userContext.emailSecondary,
        land_situation: userContext.landSituation,
        lead_status: userContext.leadStatus,
        build_situation: apiContext.buildSituation,
      });

      if (result.errors) {
        if (result.errors.name) {
          setFirstNameError(result.errors.name[0]);
        }
        if (result.errors.surname) {
          setLastNameError(result.errors.surname[0]);
        }
        if (result.errors.email) {
          setEmailError(result.errors.email[0]);
        }
        if (result.errors.phone) {
          setPhoneError(result.errors.phone[0]);
        }
        if (result.errors.region) {
          setRegionError(result.errors.region[0]);
        }
        if (result.errors.build_situation) {
          setBuildSituationError(result.errors.build_situation[0]);
        }
      } else if (result.error) {
        alert(result.error);
      } else {
        if(!IS_DEV){
          try {
          // @ts-ignore
            window.gtag("event", "SUCCEEDED", {
              "event_category": "ENQUIRY", 
              "event_label": "pyht-sign-up"
            });
            // @ts-ignore
            window.spdt("signup", {
              event_id: "pyht-sign-up",
            });
            // @ts-ignore
            window.ttq.track("SubmitForm", {
              "contents": [
                {
                  "content_id": window.location.href,
                  "content_type": "Form Submitted Successfully",
                  "content_name": "pyht-final-details"
                }
              ]
            });
            // @ts-ignore
            window.fbq("track", "SubmitApplication");
            // @ts-ignore
            uet_report_conversion();
            // @ts-ignore
            if(window.adroll !== undefined){
            // @ts-ignore
              window.adroll.identify_email(userContext.email);
            }
          } catch (e) {
            console.error(e);
          }
        }
        navContext.goNext();
      }
    } catch (e: any) {
      alert(e.message);
    }
  };

  const getRegionOptions = () => {
    const options = [];
    for (const region of apiContext.nswRegionResults) {
      options.push(region);
    }
    return options;
  };

  const onClickPromotion = (item: Promotion) => {
    if (
      userContext.selectedSpecialOffer &&
        userContext.selectedSpecialOffer.id === item.id
    ) {
      if(!item.preselected){
        userContext.setSelectedSpecialOffer(null);
        navContext.setOfferTickerVisible(false);
      }else{
        scrollToWithOffset(".screen-final-details", -50);
      }
    } else {
      userContext.setSelectedSpecialOffer(item);
      scrollToWithOffset(".screen-final-details", -50);
      //navContext.setOfferTickerVisible(true);
    }
  };

  return (
    <div className={"screen"}>
      <div className={"screen-choose-special-offers"}>
        <div className={"container"}>
          <h2 className="h1"><strong className="number">Step 6:</strong> {getPageTitle(RoutePath.SPECIAL_OFFERS)}</h2>
          <p className={"subtitle"}>
            Select our current promotion, available only for a limited time.
          </p>
          <div className="flex-section-label">
            <p className={"section-label"}>Select your Special Offers</p>
            {userContext.selectedSpecialOffer && (
              <p className={"section-label"}>1 Active</p>
            )}
          </div>
        </div>
        <PromotionSelector
          promotions={userContext.specialOffers}
          selectedPromotion={userContext.selectedSpecialOffer}
          onClickPromotion={onClickPromotion}
        />
      </div>
      <div className="screen-final-details screen-nsw-final-details container">
        {userContext.isKioskMode ? (
          <>
            <h2 className="h1"><strong className="number">Step 7:</strong> Get a copy of your new home selections!</h2>
            <p className={"subtitle"}>
              To receive an email copy of your selections enter the details below: 
            </p>
          </>
        ) : (
          <>
            <h2 className="h1"><strong className="number">Step 7:</strong> Receive a summary of your new home!</h2>
            <p className={"subtitle"}>
              To view your detailed estimate and receive an email copy of your selections enter the details below: 
            </p>
          </>
        )}

        <form className={"final-details-form"}>
          <div className={"form-group fname half"}>
            <p className={"label"}>First Name*</p>
            <div className={"form-controls"}>
              <TextInput
                placeholder={"Enter First Name"}
                value={userContext.firstName}
                onChange={(value) => userContext.setFirstName(value)}
                onScreenKeyboard={userContext.isKioskMode}
                disabled={apiContext.isLoading}
              />
              {firstNameError !== "" && (
                <p className={"error-msg"}>{firstNameError}</p>
              )}
            </div>
          </div>
          <div className={"form-group lname half"}>
            <p className={"label"}>Last Name*</p>
            <div className={"form-controls"}>
              <TextInput
                placeholder={"Enter Last Name"}
                value={userContext.lastName}
                onChange={(value) => userContext.setLastName(value)}
                onScreenKeyboard={userContext.isKioskMode}
                disabled={apiContext.isLoading}
              />
              {lastNameError !== "" && (
                <p className={"error-msg"}>{lastNameError}</p>
              )}
            </div>
          </div>
          <div className={"form-group email half"}>
            <p className={"label"}>Primary Email*</p>
            <div className={"form-controls"}>
              <TextInput
                placeholder={"Enter Email"}
                value={userContext.email}
                onChange={(value) => userContext.setEmail(value)}
                onScreenKeyboard={userContext.isKioskMode}
                disabled={apiContext.isLoading}
              />
              {emailError !== "" && (
                <p className={"error-msg"}>{emailError}</p>
              )}
            </div>
          </div>
          {userContext.isKioskMode && (
            <div className={"form-group email half"}>
              <p className={"label"}>Secondary Email</p>
              <div className={"form-controls"}>
                <TextInput
                  placeholder={"Enter Email"}
                  value={userContext.emailSecondary}
                  onChange={(value) => userContext.setEmailSecondary(value)}
                  onScreenKeyboard={userContext.isKioskMode}
                  disabled={apiContext.isLoading}
                />
              </div>
            </div>
          )}
          <div className={"form-group phone half"}>
            <p className={"label"}>Mobile Phone*</p>
            <div className={"form-controls"}>
              <TextInput
                placeholder={"Enter Mobile Phone"}
                value={userContext.phone}
                onChange={(value) => userContext.setPhone(value)}
                onScreenKeyboard={userContext.isKioskMode}
                disabled={apiContext.isLoading}
              />
              {phoneError !== "" && (
                <p className={"error-msg"}>{phoneError}</p>
              )}
            </div>
          </div>
          <div className={"form-group region"}>
            <p className={"label"}>Preferred Build Area*</p>
            <div className={"form-controls"}>
              {getRegionOptions().map((item) => {
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={`btn ${
                      apiContext.region && apiContext.region.id === item.id ? "" : "btn-secondary"
                    }`}
                    onClick={() => apiContext.setRegion(item)}
                  >
                    {item.title}
                  </button>
                );
              })}
              {regionError !== "" && (
                <p className={"error-msg"}>{regionError}</p>
              )}
            </div>
          </div>
          <div className={"form-group situation"}>
            <p className={"label"}>Tell Us About Yourself</p>
            <div className={"form-controls"}>
              <button
                type="button"
                className={`btn ${
                  apiContext.buildSituation === BuildSituation.FIRST_HOME_BUYER ? "" : "btn-secondary"
                }`}
                onClick={() => apiContext.setBuildSituation(BuildSituation.FIRST_HOME_BUYER)}
              >
                First Home Buyer
              </button>
              <button
                type="button"
                className={`btn ${
                  apiContext.buildSituation === BuildSituation.KNOCK_DOWN_REBUILD ? "" : "btn-secondary"
                }`}
                onClick={() => apiContext.setBuildSituation(BuildSituation.KNOCK_DOWN_REBUILD)}
              >
                Knock Down Rebuild/ Upsizing
              </button>
              <button
                type="button"
                className={`btn ${
                  apiContext.buildSituation === BuildSituation.INVESTOR ? "" : "btn-secondary"
                }`}
                onClick={() => apiContext.setBuildSituation(BuildSituation.INVESTOR)}
              >
                Investor
              </button>
              {buildSituationError !== "" && (
                <p className={"error-msg"}>{buildSituationError}</p>
              )}
            </div>
          </div>
          {userContext.isKioskMode ? (
            <>
              <div className={"form-group land-situation"}>
                <p className={"label"}>Land Situation</p>
                <div className={"form-controls"}>
                  {[
                    LAND_SITUATION_NSW_KIOSK_LOOKING,
                    LAND_SITUATION_NSW_KIOSK_HAVE,
                  ].map((item) => {
                    return (
                      <button
                        type="button"
                        key={item}
                        className={`btn ${
                          userContext.landSituation !== item
                            ? "btn-secondary"
                            : ""
                        }`}
                        onClick={() => userContext.setLandSituation(item)}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={"form-group land-situation"}>
                <p className={"label"}>What best describes your situation?</p>
                <div className={"form-controls land-situation-options"}>
                  {[
                    LAND_SITUATION_NSW_DESKTOP_VACANT,
                    LAND_SITUATION_NSW_DESKTOP_LOOKING,
                    LAND_SITUATION_NSW_DESKTOP_KDR,
                    LAND_SITUATION_NSW_DESKTOP_HL,
                  ].map((item) => {
                    return (
                      <div key={item} className="land-situation-option">
                        <button
                          type="button"
                          className={`btn ${
                            userContext.landSituation !== item
                              ? "btn-secondary"
                              : ""
                          }`}
                          onClick={() => userContext.setLandSituation(item)}
                        >
                          <Icon name={item} />
                          <span>{item}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          <div className="form-bottom">
            <div className="checkboxes">
              {!userContext.isKioskMode && (
                <div className={"form-group form-remember"}>
                  <div className={"form-controls"}>
                    <Checkbox
                      checked={userContext.rememberDetails}
                      onChange={(value) => userContext.setRememberDetails(value)}
                      disabled={apiContext.isLoading}
                    >
                      <>
                    Remember my details for next time
                      </>
                    </Checkbox>
                  </div>
                </div>
              )}
              <div className={"form-group form-remember"}>
                <div className={"form-controls"}>
                  <Checkbox
                    checked={userContext.interestedInGrannyFlats}
                    onChange={(value) => userContext.setInterestedInGrannyFlats(value)}
                    disabled={apiContext.isLoading}
                  >
                    <>
                      {"I'm interested in granny flats"}
                    </>
                  </Checkbox>
                </div>
              </div>
              <div className={"form-group form-remember"}>
                <div className={"form-controls"}>
                  <Checkbox
                    checked={userContext.sendToFinanceManager}
                    onChange={(value) => userContext.setSendToFinanceManager(value)}
                    disabled={apiContext.isLoading}
                  >
                    <>
                      I would like a free finance consultation
                    </>
                  </Checkbox>
                </div>
              </div>
              <div className={"form-group form-remember"}>
                <div className={"form-controls"}>
                  <Checkbox
                    checked={userContext.consent}
                    onChange={(value) => userContext.setConsent(value)}
                    disabled={apiContext.isLoading}
                  >
                    <>
                      { apiContext.finalDetailsSettings.form_disclaimer }
                    </>
                  </Checkbox>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default NswFinalDetailsScreen;
